
import type { Column, SortDirection } from '../simple-table'
import type { Diagnosis } from './diagnosis'

import * as Sentry from '@sentry/react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import cx from 'classnames'
import * as d3 from 'd3'
import { useEffect, useCallback, useMemo, useState, useContext, useRef, useLayoutEffect } from 'react'
import { MdOutlineViewList, MdOutlineScatterPlot } from 'react-icons/md'
import { Link, useNavigate } from 'react-router-dom'
import { useDebounce } from 'react-use'

import { CommentIcon } from '../../../assets/svgs/components'
import { ClientNameContext } from '../../../lib/context/client-name'
import { PatientIdContext } from '../../../lib/context/patient-id'
import http from '../../../lib/http'
import { useAppSelector } from '../../../store'
import { ErrorToast } from '../../shared/error-toast'
import { Filters } from '../components/filters'
import { useDebouncedFilters } from '../hooks/use-debounced-filters'
import * as Table from '../simple-table'

import styles from './index.module.css'
import { diagnosesSchema } from './schema'
import { Status, statusNameMapper } from './status.enum'

let numberFormatter = new Intl.NumberFormat('en-US')

interface DiagnosisActionsProps {
  item: Diagnosis
  onRefetch?: () => Promise<unknown> | unknown
  onError?: (message: string) => Promise<unknown> | unknown
}

interface DiagnosisActionsState {
  isLoading: boolean
  error?: Error
  currentAction?: 'add' | 'ignore' | 'restore' | 'comment'
}

function DiagnosisActions ({ item, onRefetch, onError }: DiagnosisActionsProps): JSX.Element | null {
  let resource = useAppSelector(state => state.globalVars?.params?.id)
  let code = item.description?.code
  let status = item.status

  let [ state, setState ] = useState<DiagnosisActionsState>({
    isLoading: false
  })
  let [ commentState, setCommentState ] = useState({ open: false, comment: item.comment ?? '' })

  let handleAction = useCallback((resource: string, code: string, action: NonNullable<DiagnosisActionsState['currentAction']>) => {
    setState(state => ({
      ...state,
      isLoading: true,
      currentAction: action
    }))

    http.postRequest(`/patients/${resource}/claims/diagnoses/${action}`, { code })
      .then(() => {
        onRefetch?.()
      })
      .catch((err: unknown) => {
        setState(state => ({
          ...state,
          error: err instanceof Error ? err : new Error('Unknown error')
        }))

        if (err instanceof AxiosError) {
          let { status, data } = err.response ?? {}

          if (status === 404 && data?.message) {
            onError?.(data.message)
          }
        }
      })
      .finally(() => {
        setState(state => ({
          ...state,
          isLoading: false,
          currentAction: undefined
        }))
      })
  }, [ onRefetch, onError ])

  function handleCommentButtonClick (): void {
    setCommentState((prev) => ({
      ...prev,
      open: !prev.open
    }))
  }

  function handleCommentOnChange (value: string): void {
    setCommentState((prev) => ({
      ...prev,
      comment: value
    }))
  }

  function handleSaveComment (): void {
    if (!resource) {
      return
    }

    setState(state => ({
      ...state,
      isLoading: true,
      currentAction: 'comment'
    }))

    setCommentState((prev) => ({
      ...prev,
      open: false
    }))

    http.postRequest(`/patients/${resource}/claims/diagnoses/comment`, { code, comment: commentState.comment })
      .then(() => {
        onRefetch?.()
      })
      .catch((err: unknown) => {
        setState(state => ({
          ...state,
          error: err instanceof Error ? err : new Error('Unknown error')
        }))

        setCommentState((prev) => ({
          ...prev,
          comment: item.comment ?? ''
        }))

        if (err instanceof AxiosError) {
          let { data } = err.response ?? {}

          if (data?.message) {
            onError?.(data.message)
          }
        }
      })
      .finally(() => {
        setState(state => ({
          ...state,
          isLoading: false,
          currentAction: undefined
        }))
      })
  }

  if (!resource || !code) {
    return null
  }

  return (
    <Table.ActionButtonWrapper>
      {status === 'new' && (
        <>
          <Table.ActionButton
            primary
            isLoading={state.isLoading && state.currentAction === 'add'}
            isDisabled={state.isLoading}
            onClick={event => {
              event.preventDefault()
              handleAction(resource as string, code as string, 'add')
            }}
          >
            Add
          </Table.ActionButton>
          <Table.ActionButton
            isLoading={state.isLoading && state.currentAction === 'ignore'}
            isDisabled={state.isLoading}
            onClick={event => {
              event.preventDefault()
              handleAction(resource as string, code as string, 'ignore')
            }}
          >
            Ignore
          </Table.ActionButton>
        </>
      )}

      {status === 'ignored' && (
        <Table.ActionButton
          isLoading={state.isLoading && state.currentAction === 'restore'}
          isDisabled={state.isLoading}
          onClick={event => {
            event.preventDefault()
            handleAction(resource as string, code as string, 'restore')
          }}
        >
          Restore
        </Table.ActionButton>
      )}

      <Table.ActionButton
        className={cx(styles.commentButtonWrapper, {
          [styles.open as string]: commentState.open
        })}

      >
        <button
          className={cx(styles.commentButton, {
            [styles.haveComment as string]: item.comment,
            [styles.open as string]: commentState.open
          })}
          onClick={handleCommentButtonClick}
        >
          <CommentIcon />
        </button>
        {commentState.open && (
          <div className={styles.commentContainer}>
            <textarea
              rows={8}
              onChange={(e) => handleCommentOnChange(e.target.value)}
              placeholder={item.comment ? 'Edit comment' : 'Enter comment'}
              value={commentState.comment}
            />
            <button
              disabled={(item.comment ?? '') === commentState.comment}
              className={styles.saveCommentButton}
              onClick={handleSaveComment}
            >
              Save
            </button>
          </div>
        )}
      </Table.ActionButton>
    </Table.ActionButtonWrapper>
  )
}

type QueryKey = [
  string,
  string | undefined,
  SearchParams
]

interface Response {
  data: Diagnosis[]
  total_filtered: number
  total_unfiltered: number
  filters: Record<string, Array<string | number>>
}

interface SearchParams extends Record<string, unknown>{
  query: string

  filters: {
    severity: number[]
    category: string[]
    chapter: string[]
    provider_name: string[]
    status: Status[]
  }

  sort: {
    field: keyof Diagnosis
    direction: SortDirection
  }

  pagination: {
    page: number
    pageSize: number
  }
}

let defaultSearchParams: SearchParams = {
  query: '',

  filters: {
    severity: [
      3,
      4
    ],
    chapter: [],
    provider_name: [],
    category: [],
    status: [
      Status.New
    ]
  },

  sort: {
    field: 'severity',
    direction: 'desc'
  },

  pagination: {
    page: 0,
    pageSize: 10
  }
}

function isDefaultSearchParams (params: SearchParams): boolean {
  return JSON.stringify(params) === JSON.stringify(defaultSearchParams)
}

interface DiagnosesClaimsTableProps {
  onAction?: () => void
  onMount?: (refetch: () => Promise<unknown> | unknown) => void
}

type Mode = 'table' | 'chart'

export function DiagnosesClaimsTable ({ onAction, onMount }: DiagnosesClaimsTableProps): JSX.Element | null {
  let [
    debouncedSearchParams,
    searchParams,
    setSearchParams,
    setDebouncedSearchParams
  ] = useDebouncedFilters<SearchParams>(defaultSearchParams)

  let [ searchQuery, setSearchQuery ] = useState('')
  let [ error, setError ] = useState<string | undefined>()
  let [ mode, setMode ] = useState<Mode>('table')

  let client = useQueryClient()
  let cardRef = useRef<HTMLDivElement>(null)
  let [ width, setWidth ] = useState(1000)

  useLayoutEffect(() => {
    setWidth(cardRef.current?.getBoundingClientRect().width ?? 1000)
  }, [])

  useDebounce(() => {
    setDebouncedSearchParams(params => ({
      ...params,
      query: searchQuery,
      pagination: {
        ...params.pagination,
        page: 0
      }
    }))
  }, 500, [ searchQuery ])

  let resource = useAppSelector(state => state.globalVars?.params?.id)

  let query = useQuery<Response | undefined, unknown, Response | undefined, QueryKey>({
    queryKey: [
      'claims-history/diagnoses',
      resource,
      debouncedSearchParams
    ],
    queryFn: async function getDiagnoses ({ queryKey }): Promise<Response | undefined> {
      let [ , resource, searchParams ] = queryKey
      let { query, filters, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let urlSearchParams = new URLSearchParams([
        [ 'on', sort.field ],
        [ 'dir', sort.direction ],
        [ 'page', pagination.page.toString(10) ],
        [ 'size', pagination.pageSize.toString(10) ],
        ...filters.severity.map(severity => [ 'severity', severity.toString() ]),
        ...filters.status.map(status => [ 'status', status ]),
        ...filters.chapter.map(chapter => [ 'chapter', chapter ]),
        ...filters.category.map(category => [ 'category', category ]),
        ...filters.provider_name.map(name => [ 'provider_name', name ])
      ])

      if (normalizedQuery.length > 0) {
        urlSearchParams.set('search', normalizedQuery)
      }

      if (!resource) {
        return
      }

      let response: Response = await http.getRequest(`/patients/${resource}/claims/diagnoses?${urlSearchParams.toString()}`)
      let data = diagnosesSchema.parse(response.data)

      return {
        ...response,
        data
      }
    },
    enabled: typeof resource === 'string',
    retry: false,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false
  })

  let { refetch } = query

  let handlePageChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: value - 1
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handlePageSizeChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0,
        pageSize: value
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handleSortChange = useCallback((field: keyof Diagnosis, direction: SortDirection): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      sort: {
        ...params.sort,
        field,
        direction
      }
    }))
  }, [ setSearchParams ])

  let handleFilterChange = useCallback((name: string, value: Array<string | number | boolean>): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      filters: {
        ...params.filters,
        [name]: value
      }
    }))
  }, [ setSearchParams ])

  let handleFiltersClear = useCallback((): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      filters: {
        ...params.filters,
        severity: [],
        category: [],
        chapter: [],
        provider_name: [],
        status: []
      }
    }))
  }, [ setSearchParams ])

  let filters = useMemo(() => {
    let keys: Array<keyof SearchParams['filters']> = [
      'severity',
      'category',
      'chapter',
      'provider_name',
      'status'
    ]

    return keys.map(key => {
      let options = (query.data?.filters[key] ?? [])
        .map((value: string | number) => {
          let label = value

          if (key === 'status') {
            label = statusNameMapper[value as Status] ?? value
          }

          return {
            label: label.toString(),
            value
          }
        })

      return {
        name: key,
        value: searchParams.filters[key],
        options
      }
    })
  }, [ query.data?.filters, searchParams.filters ])

  const onRefetch = useCallback(async () => {
    await client.invalidateQueries({
      predicate ({ queryKey }) {
        return Array.isArray(queryKey) && queryKey[0] === 'claims-history/diagnoses'
      }
    })

    await query.refetch()
    onAction?.()
  }, [
    query,
    client,
    onAction
  ])

  useEffect(() => {
    onMount?.(refetch)
  }, [ onMount, refetch ])

  const onError = useCallback((message: string): void => {
    setError(message)
    setTimeout(() => setError(undefined), 10000)
  }, [])

  let columns = useMemo<Array<Column<Diagnosis>>>(() => [
    {
      name: 'description',
      header: 'Diagnosis',
      gridArea: 'minmax(12rem, 2fr)',
      isSortable: true,
      getValue ({ description }) {
        if (!description) {
          return null
        }

        let { code, descriptor } = description

        return (
          <span>
            {typeof code === 'string' && (
              <>
                <Link
                  to={`../claims-history?code=${encodeURIComponent(code)}&force=code`}
                  className={styles.diagnosisLink}
                >
                  <strong>{code}</strong>
                </Link>

                {' '}
              </>
            )}
            {descriptor}
          </span>
        )
      }
    },
    {
      name: 'category',
      header: (
        <div
          title='Disease Category'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          Category
        </div>
      ),
      gridArea: 'minmax(12rem, 2fr)',
      isSortable: true
    },
    {
      name: 'chapter',
      header: 'Chapter',
      isSortable: true,
      gridArea: '0.5fr'
    },
    {
      name: 'claims',
      header: (
        <div
          title='Number of Claims'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit',
            alignItems: 'flex-end'
          }}
        >
          Claims
        </div>
      ),
      isSortable: true,
      initialSortDirection: 'desc',
      style: {
        textAlign: 'right'
      },
      gridArea: 'minmax(4rem, 0.6fr)'
    },
    {
      name: 'provider_name',
      header: 'Providers',
      isSortable: true,
      getValue: item => item.provider_name
        ?.filter(Boolean)
        .join(', ')
    },
    {
      name: 'first_claim_date',
      header: (
        <div
          title='First Claim Date'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          First
        </div>
      ),
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (item) {
        if (typeof item.first_claim_date !== 'string') {
          return null
        }

        return Table.formatDate(item.first_claim_date)
      },
      gridArea: 'minmax(6rem, 1fr)'
    },
    {
      name: 'last_claim_date',
      header: (
        <div
          title='Last Claim Date'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          Last
        </div>
      ),
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (item) {
        if (typeof item.last_claim_date !== 'string') {
          return null
        }

        return Table.formatDate(item.last_claim_date)
      },
      gridArea: 'minmax(6rem, 1fr)'
    },
    {
      name: 'severity',
      header: 'Severity',
      gridArea: 'minmax(6rem, 0.5fr)',
      isSortable: true,
      initialSortDirection: 'desc',
      style: {
        textAlign: 'right'
      }
    },
    {
      name: 'status',
      header: 'Status',
      isSortable: true,
      initialSortDirection: 'asc',
      getValue (item) {
        return typeof item.status === 'string'
          ? statusNameMapper[item.status] ?? '–'
          : '–'
      }
    },
    {
      name: 'actions',
      header: 'Actions',
      getValue (item) {
        return (
          <DiagnosisActions
            item={item}
            onRefetch={onRefetch}
            onError={onError}
          />
        )
      }
    }
  ], [ onRefetch, onError ])

  return (
    <div ref={cardRef}>
      <Table.Table
        header={(
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexGrow: 1,
                minHeight: '4.5rem'
              }}
            >
              <h2>Claims History | Diagnoses</h2>
              {query.status === 'success' && mode === 'table' && (
                <div>
                  <Filters
                    search={searchQuery}
                    onSearchChange={setSearchQuery}
                  />
                </div>
              )}
            </div>

            <div className={styles.tabs}>
              <button
                type='button'
                className={cx(styles.tabButton, {
                  [styles.active as string]: mode === 'table'
                })}
                onClick={() => {
                  setMode('table')
                }}
              >
                <MdOutlineViewList
                  size='1rem'
                  color='#777'
                /> Table
              </button>
              <button
                type='button'
                className={cx(styles.tabButton, {
                  [styles.active as string]: mode === 'chart'
                })}
                onClick={() => {
                  if ((query.data?.total_unfiltered ?? 0) === 0) {
                    return
                  }

                  setMode('chart')
                }}
                disabled={(query.data?.total_unfiltered ?? 0) === 0}
                title={(query.data?.total_unfiltered ?? 0) === 0 ? 'No data to display' : undefined}
              >
                <MdOutlineScatterPlot
                  size='1rem'
                  color='#777'
                /> Heatmap
              </button>
            </div>
          </>
        )}
        columns={columns}
        data={query.data?.data ?? []}
        sort={searchParams.sort}
        pagination={{
          ...searchParams.pagination,
          totalFiltered: query.data?.total_filtered,
          totalUnfiltered: query.data?.total_unfiltered
        }}
        filters={filters}
        error={query.error as Error}
        queryStatus={query.status}
        fetchStatus={query.fetchStatus}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onRefetch={() => {
          if (isDefaultSearchParams(searchParams)) {
            return query.refetch()
          }

          client.invalidateQueries({
            queryKey: [
              'claims-history/diagnoses',
              resource
            ]
          })
            .then(() => {
              setSearchParams(defaultSearchParams)
            })
            .catch(err => {
              console.error(err)
              Sentry.captureException(err)
            })
        }}
        wrapperStyle={{
          backgroundColor: '#ffffff',
          position: 'relative',
          zIndex: 1
        }}
        children={mode === 'chart'
          ? <Chart width={width} />
          : undefined
        }
        hideBorders={mode !== 'chart'}
      />
      <ErrorToast message={error} />
    </div>
  )
}

interface ChartProps {
  width: number
}

function Chart ({ width }: ChartProps): JSX.Element | null {
  type QueryKey = [
    'claims-history/diagnoses:chart',
    string | undefined,
    string | undefined
  ]

  interface Entry {
    code: string
    descriptor: string
    category: string
    chapter: string
    claims: number
  }

  let patientId = useContext(PatientIdContext)
  let clientName = useContext(ClientNameContext)
  let navigate = useNavigate()

  let query = useQuery<Entry[], unknown, Entry[], QueryKey>({
    queryKey: [
      'claims-history/diagnoses:chart',
      clientName,
      patientId
    ],

    async queryFn ({ queryKey }) {
      let [ , clientName, patientId ] = queryKey

      if (typeof patientId !== 'string' || typeof clientName !== 'string') {
        return []
      }

      let response = await http.getRequest(`/patients/${patientId}/claims/diagnoses/chart`, {}, {
        clientName
      })

      return response as Entry[]
    },
    enabled: typeof patientId === 'string' && typeof clientName === 'string',
    placeholderData: keepPreviousData
  })

  // let width = 928
  let height = width * 3 / 4
  let margin = 10

  function group (d: Entry): string {
    return d.chapter
  }

  let color = d3.scaleOrdinal(d3.schemeTableau10)

  let pack = d3.pack<Entry>()
    .size([ width - margin * 2, height - margin * 2 ])
    .padding(3)

  let hierarchy = d3.hierarchy({ children: query.data ?? [] })

  // @ts-expect-error // TODO: fix this, typings are somehow wrong
  let root = pack(hierarchy.sum(d => d.claims))

  let categories = root.leaves().map(leaf => group(leaf.data))
    .filter(Boolean)
    .sort((a, b) => d3.ascending(a, b))
    .filter((v, i, a) => a.indexOf(v) === i)

  if (query.isLoading) {
    return null
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`-${margin} -${margin} ${width} ${height}`}
      style={{
        maxWidth: '100%'
      }}
      textAnchor='middle'
      fontFamily='Roboto, sans-serif'
      fontSize={10}
    >
      {root.leaves().map((leaf, i) => (
        <g
          key={i}
          transform={`translate(${leaf.x + 1}, ${leaf.y + 1})`}
        >
          <circle
            r={leaf.r}
            fill={color(group(leaf.data))}
            fillOpacity={0.25}
            style={{
              cursor: 'pointer'
            }}
            onClick={() => {
              let code = leaf.data.code

              if (typeof code !== 'string') {
                return
              }

              navigate(`../claims-history?code=${encodeURIComponent(code)}&force=code`)
            }}
          />

          <title>
            {[ leaf.data.code, leaf.data.descriptor ].filter(Boolean).join(': ')} {'\n'}
            {[ leaf.data.chapter, leaf.data.category ].filter(Boolean).join(': ')} {'\n'}
            {numberFormatter.format(leaf.data.claims)} {leaf.data.claims === 1 ? 'claim' : 'claims'}
          </title>

          <text
            clipPath={`circle(${leaf.r - 1}px at center)`}
          >
            <tspan
              x={0}
              y={`${-0.5 + 0.35}em`}
              fontSize={11}
            >
              {leaf.data.code}
            </tspan>

            <tspan
              x={0}
              y={`${0.5 + 0.35}em`}
              fillOpacity={0.75}
              fontSize={10}
            >
              {numberFormatter.format(leaf.data.claims)}
            </tspan>
          </text>
        </g>
      ))}

      <g
        x={width - margin * 2}
        y={height - margin * 2}
        transform='translate(24, 24)'
        textAnchor='start'
        fontSize={10}
      >
        <text
          x={0}
          y={10}
          fontSize='1rem'
        >
          Chapter
        </text>
        {categories.map((category, i) => (
          <g
            key={category}
            transform={`translate(8, ${(i * 25) + 40})`}
          >
            <circle
              r={8}
              fill={color(category)}
              fillOpacity={0.25}
            />

            <text
              x={18}
              y={0}
              fontSize='0.75rem'
              dy='0.35em'
            >
              {category}
            </text>
          </g>
        ))}
      </g>
    </svg>
  )
}
